import React, { useState } from 'react';
import { FileText, ChevronDown, ChevronRight, Plus, Edit2 } from 'lucide-react';
import { useDocuments } from '../hooks/useDocuments';
import { DocumentEditor } from './documents/DocumentEditor';
import { DocumentViewer } from './documents/DocumentViewer';
import type { Document } from '../types';

interface TodoFormDocumentProps {
  todoId: string;
  todoTitle: string;
}

const TodoFormDocument: React.FC<TodoFormDocumentProps> = ({ todoId, todoTitle }) => {
  const { documents, createDocument, updateDocument, isCreating, isUpdating } = useDocuments({ task_id: todoId });
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreatingNew, setIsCreatingNew] = useState(false);

  const linkedDoc: Document | undefined = documents[0];

  const handleCreate = async (data: { title: string; body: string }) => {
    await createDocument({
      title: data.title,
      body: data.body,
      owner: 'user',
      task_id: todoId,
    });
    setIsCreatingNew(false);
  };

  const handleUpdate = async (data: { title: string; body: string; is_pinned?: boolean }) => {
    if (!linkedDoc) return;
    await updateDocument({ id: linkedDoc.id, update: data });
    setIsEditing(false);
  };

  // Creating new document
  if (isCreatingNew) {
    return (
      <div className="mt-3 border border-blue-200 dark:border-blue-800 rounded-md p-3 bg-blue-50/30 dark:bg-blue-900/10">
        <div className="flex items-center gap-2 mb-2">
          <FileText className="w-4 h-4 text-blue-500" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">New Document</span>
        </div>
        <DocumentEditor
          initialTitle={todoTitle}
          hideTitle
          onSave={handleCreate}
          onCancel={() => setIsCreatingNew(false)}
          isSaving={isCreating}
        />
      </div>
    );
  }

  // No document yet — show add button
  if (!linkedDoc) {
    return (
      <div className="mt-3">
        <button
          type="button"
          onClick={() => setIsCreatingNew(true)}
          className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          Add document
        </button>
      </div>
    );
  }

  // Editing existing document
  if (isEditing) {
    return (
      <div className="mt-3 border border-blue-200 dark:border-blue-800 rounded-md p-3 bg-blue-50/30 dark:bg-blue-900/10">
        <div className="flex items-center gap-2 mb-2">
          <FileText className="w-4 h-4 text-blue-500" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Edit Document</span>
        </div>
        <DocumentEditor
          initialTitle={linkedDoc.title}
          initialBody={linkedDoc.body}
          initialPinned={linkedDoc.is_pinned}
          onSave={handleUpdate}
          onCancel={() => setIsEditing(false)}
          isSaving={isUpdating}
          showPinToggle
        />
      </div>
    );
  }

  // Document exists — show collapsible preview
  return (
    <div className="mt-3 border border-gray-200 dark:border-gray-600 rounded-md overflow-hidden">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
      >
        {isExpanded ? (
          <ChevronDown className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
        ) : (
          <ChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
        )}
        <FileText className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
        <span className="text-sm text-gray-700 dark:text-gray-300 truncate flex-1">{linkedDoc.title}</span>
        {linkedDoc.agent_edited && linkedDoc.owner === 'user' && (
          <span className="text-[10px] text-amber-600 dark:text-amber-400 flex-shrink-0">(agent edited)</span>
        )}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setIsEditing(true); }}
          className="p-1 text-gray-400 hover:text-blue-500 transition-colors flex-shrink-0"
          title="Edit document"
        >
          <Edit2 className="w-3.5 h-3.5" />
        </button>
      </button>

      {isExpanded && (
        <div className="px-3 pb-3 border-t border-gray-200 dark:border-gray-600">
          <DocumentViewer document={linkedDoc} />
        </div>
      )}
    </div>
  );
};

export default TodoFormDocument;
